import { Box, Fade, Paper, Popper, PopperPlacementType } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "./index.css";
import QuestionLanguages from "../../pages/exams/QuestionLanguages";
import mathFunction from "../../pages/exams/mathFunction";

function MessagePopper({
  text,
  id,
  type,
  content,
  question,
  fetchSingleQuestionDetails,
}: {
  text?: string;
  id: string;
  type?: "ck";
  content?: any;
  question?: any;
  fetchSingleQuestionDetails?: any;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [overflow, setOverflow] = useState(false);

  const paragraphRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => placement !== "bottom" || !prev);
    setPlacement("bottom");
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        if (!question) handleClose();
      }
    };
    document.body.addEventListener("click", handleOutsideClick);
    return () => document.body.removeEventListener("click", handleOutsideClick);
  }, [anchorEl, question]);

  useEffect(() => {
    const addEllipsisIfNeeded = async () => {
      const paragraph = paragraphRef.current;
      if (!paragraph) return;

      const isOverflowing = (el: HTMLElement) =>
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

      const findLastVisibleWord = async (words: string[]) => {
        let left = 0;
        let right = words.length - 1;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          paragraph.textContent = words.slice(0, mid + 1).join(" ");
          await new Promise((resolve) => requestAnimationFrame(resolve));

          if (isOverflowing(paragraph)) {
            right = mid - 1;
          } else {
            left = mid + 1;
          }
        }
        return right;
      };

      if (paragraph.textContent && isOverflowing(paragraph)) {
        const words = paragraph.textContent.split(" ");
        const lastVisibleIndex = await findLastVisibleWord(words);
        paragraph.textContent = words.slice(0, lastVisibleIndex + 1).join(" ") + "...";
        setOverflow(true);
      }

      mathFunction(); // typeset math after truncation
    };

    if (text) addEllipsisIfNeeded();
  }, [id, text]);

  return (
    <>
      <Popper
        sx={{
          zIndex: 1200,
          m: "-20px 20px 0",
          boxShadow: "0px 0px 4px rgba(0,0,0,0.25)",
          maxWidth: question ? "420px" : "300px",
          borderRadius: "8px",
          maxHeight: "calc(100% - 150px)",
        }}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={{ p: "10px", fontSize: "0.75rem" }} className="all-content">
              {question ? (
                <QuestionLanguages
                  handleClose={handleClose}
                  fetchSingleQuestionDetails={fetchSingleQuestionDetails}
                  question={question}
                />
              ) : type ? (
                <div dangerouslySetInnerHTML={{ __html: text ?? "" }} />
              ) : (
                text
              )}
            </Paper>
          </Fade>
        )}
      </Popper>

      <Box
        ref={paragraphRef}
        sx={{
          whiteSpace: "pre-wrap",
          maxHeight: "98px",
          width: "100%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          cursor: overflow || question ? "pointer" : "inherit",
          fontSize: "0.75rem",
        }}
        onClick={(e: any) => {
          if (overflow || question) handleClick(e);
        }}
      >
        {content ? (
          content
        ) : type ? (
          <div dangerouslySetInnerHTML={{ __html: text ?? "" }} />
        ) : (
          text
        )}
      </Box>
    </>
  );
}

export default MessagePopper;
